// UploadContext - root-level upload manager so uploads survive navigation.

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ANALYZER_ARCHIVE_MAX_BYTES } from '@/lib/api';

// App-wide upload manager at React root, so uploads (and their XHRs)
// survive navigation. Two run-time paths:
//   - single PUT (≤ 4.5 GB): one presigned URL.
//   - multipart (> 4.5 GB to R2): N parallel presigned UploadPart calls.

type UploadPhase = 'hashing' | 'uploading' | 'committing';
type UploadStatus = UploadPhase | 'success' | 'error' | 'canceled';

/** Which artifact is being uploaded. The transport is identical for both -
 *  hash, ask for a URL, PUT, commit - and this only selects the two request
 *  bodies and the size ceiling. Absent means a VM base image, which is what
 *  every caller sent before analyzer archives existed. */
export type UploadKind = 'analyzer_archive';

export interface UploadTask {
  id: string;
  fileName: string;
  imageName: string;
  size: number;
  loaded: number;
  status: UploadStatus;
  error?: string;
  startedAt: number;
  endedAt?: number;
  digest?: string;
  osFamily?: string;
  kind?: UploadKind;
}

interface UploadApi {
  tasks: UploadTask[];
  start: (file: File, imageName: string, opts?: { osFamily?: string; kind?: UploadKind }) => string;
  cancel: (id: string) => void;
  dismiss: (id: string) => void;
  dismissCompleted: () => void;
  onCompleted: (cb: (task: UploadTask) => void) => () => void;
}

const Ctx = createContext<UploadApi | null>(null);

export function useUploads(): UploadApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useUploads must be used inside <UploadProvider>');
  return v;
}

// R2 single-PUT is capped at 5 GiB; margin avoids a hard-boundary reject.
const SINGLE_PUT_LIMIT = 4.5 * 1024 * 1024 * 1024;
// R2 caps ~50 Mbps per connection; concurrency only helps when the browser
// opens parallel TCP (HTTP/1.1, 6/origin). 8 parts × ~50 Mbps ≈ 400 Mbps.
const PART_SIZE = 50 * 1024 * 1024;
const PART_CONCURRENCY = 8;

type TaskRefs = {
  xhrs: React.RefObject<Map<string, XMLHttpRequest>>;
};

export function UploadProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasks] = useState<UploadTask[]>([]);
  const xhrs = useRef<Map<string, XMLHttpRequest>>(new Map());
  const aborters = useRef<Map<string, AbortController>>(new Map());
  // Per-task accumulators for multipart progress - XHR onprogress
  // fires per-part, but the widget shows the cross-part total.
  const partProgress = useRef<Map<string, number[]>>(new Map());
  const completedSubscribers = useRef<Set<(t: UploadTask) => void>>(new Set());

  const update = useCallback((id: string, patch: Partial<UploadTask>) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }, []);

  const run = useCallback(
    async (task: UploadTask, file: File) => {
      const ac = new AbortController();
      aborters.current.set(task.id, ac);

      try {
        const refusal = analyzerArchiveRefusal(task.kind, file.size);
        if (refusal) throw new Error(refusal);
        const sha = await hashFileSHA256(file, ac.signal, (loaded) =>
          update(task.id, { loaded }),
        );
        if (ac.signal.aborted) return;

        // Only a base image can reach the multipart path. The archive ceiling
        // the refusal above applies is well under SINGLE_PUT_LIMIT, and the
        // orchestrator refuses an archive at both multipart handlers anyway.
        const useMultipart = file.size > SINGLE_PUT_LIMIT;
        update(task.id, { status: 'uploading', loaded: 0 });

        if (useMultipart) {
          await runMultipart(task, file, sha, ac, update, { xhrs }, partProgress);
        } else {
          await runSinglePut(task, file, sha, ac, update, { xhrs });
          if (ac.signal.aborted) return;
          update(task.id, { status: 'committing' });
          const commitResp = await fetch('/api/admin/images/presign-commit', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(commitBody(task, sha)),
            signal: ac.signal,
          });
          if (!commitResp.ok) throw new Error(await readError(commitResp));
        }

        const finishedTask: UploadTask = {
          ...task,
          loaded: file.size,
          status: 'success',
          endedAt: Date.now(),
          digest: sha,
        };
        setTasks((prev) => prev.map((t) => (t.id === task.id ? finishedTask : t)));
        completedSubscribers.current.forEach((cb) => {
          cb(finishedTask);
        });
      } catch (e) {
        if (ac.signal.aborted) {
          update(task.id, { status: 'canceled', endedAt: Date.now() });
          return;
        }
        const msg = e instanceof Error ? e.message : String(e);
        update(task.id, { status: 'error', error: msg, endedAt: Date.now() });
      } finally {
        aborters.current.delete(task.id);
        xhrs.current.delete(task.id);
        partProgress.current.delete(task.id);
      }
    },
    [update],
  );

  const start = useCallback(
    (file: File, imageName: string, opts?: { osFamily?: string; kind?: UploadKind }) => {
      const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      const task: UploadTask = {
        id,
        fileName: file.name,
        imageName,
        size: file.size,
        loaded: 0,
        status: 'hashing',
        startedAt: Date.now(),
        osFamily: opts?.osFamily,
        kind: opts?.kind,
      };
      setTasks((prev) => [...prev, task]);
      void run(task, file);
      return id;
    },
    [run],
  );

  const cancel = useCallback((id: string) => {
    const ac = aborters.current.get(id);
    if (ac) ac.abort();
    const xhr = xhrs.current.get(id);
    if (xhr) xhr.abort();
  }, []);

  const dismiss = useCallback((id: string) => {
    setTasks((prev) =>
      prev.filter((t) => t.id !== id || isInFlight(t.status)),
    );
  }, []);

  const dismissCompleted = useCallback(() => {
    setTasks((prev) => prev.filter((t) => isInFlight(t.status)));
  }, []);

  const onCompleted = useCallback((cb: (t: UploadTask) => void) => {
    completedSubscribers.current.add(cb);
    return () => {
      completedSubscribers.current.delete(cb);
    };
  }, []);

  const api = useMemo<UploadApi>(
    () => ({ tasks, start, cancel, dismiss, dismissCompleted, onCompleted }),
    [tasks, start, cancel, dismiss, dismissCompleted, onCompleted],
  );

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function isInFlight(status: UploadStatus): boolean {
  return status === 'hashing' || status === 'uploading' || status === 'committing';
}

// The three places the two artifacts differ. Everything else about an upload -
// the hash, the presign, the PUT, the progress, the cancel - is the same code
// for both, which is why this is three small functions rather than a second
// upload manager.

// initBody and commitBody: an archive carries a kind and nothing else. It has
// no operator-chosen name and no OS family, because every fact recorded about
// it is read out of the archive itself at commit.
export function initBody(task: UploadTask, sha: string, size: number): Record<string, unknown> {
  if (task.kind === 'analyzer_archive') {
    return { sha256: sha, size, kind: task.kind, content_type: ANALYZER_ARCHIVE_CONTENT_TYPE };
  }
  return { sha256: sha, size, name: task.imageName, content_type: BASE_IMAGE_CONTENT_TYPE };
}

export function commitBody(task: UploadTask, sha: string): Record<string, unknown> {
  if (task.kind === 'analyzer_archive') {
    return { sha256: sha, kind: task.kind };
  }
  return {
    sha256: sha,
    name: task.imageName,
    content_type: BASE_IMAGE_CONTENT_TYPE,
    os_family: task.osFamily ?? '',
  };
}

// The archive presign binds this into the signature rather than taking the
// client's word for it, so a PUT that sends anything else is refused by the
// storage backend with a signature error rather than by us with a sentence.
const ANALYZER_ARCHIVE_CONTENT_TYPE = 'application/x-tar';
const BASE_IMAGE_CONTENT_TYPE = 'application/octet-stream';

export function uploadContentType(task: UploadTask): string {
  return task.kind === 'analyzer_archive' ? ANALYZER_ARCHIVE_CONTENT_TYPE : BASE_IMAGE_CONTENT_TYPE;
}

function formatGiB(bytes: number): string {
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GiB`;
}

/**
 * Why an archive of this size cannot be uploaded, or null when it can.
 *
 * Refuses rather than truncates, and refuses before the hash rather than after
 * the upload: the orchestrator answers 413 at presign for an archive over the
 * cap and refuses the multipart path for one at any size, so the alternative is
 * an operator watching a progress bar fill for something that was never going
 * to be accepted.
 *
 * At the cap exactly is accepted, because that is what the server accepts.
 */
export function analyzerArchiveRefusal(kind: UploadKind | undefined, size: number): string | null {
  if (kind !== 'analyzer_archive' || size <= ANALYZER_ARCHIVE_MAX_BYTES) return null;
  return (
    `Analyzer archives are limited to ${formatGiB(ANALYZER_ARCHIVE_MAX_BYTES)}, and this one is ` +
    `${formatGiB(size)}. Save a smaller image, or push it to a registry and register the ` +
    `reference instead.`
  );
}

// runSinglePut handles the small-file path: one init RPC → one PUT
// → caller calls commit. Throws on any non-2xx; abort signal cancels.
async function runSinglePut(
  task: UploadTask,
  file: File,
  sha: string,
  ac: AbortController,
  update: (id: string, patch: Partial<UploadTask>) => void,
  refs: TaskRefs,
) {
  const initResp = await fetch('/api/admin/images/presign-init', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(initBody(task, sha, file.size)),
    signal: ac.signal,
  });
  if (!initResp.ok) throw new Error(await readError(initResp));
  const init = (await initResp.json()) as { upload_url?: string; already_stored?: boolean };
  if (init.already_stored) {
    update(task.id, { loaded: file.size });
    return;
  }
  if (!init.upload_url) throw new Error('presign-init returned no upload URL');
  // Validate the URL origin before sending file bytes (FE-SEC-02).
  await validatePresignedUrl(init.upload_url, ac.signal);
  const checksum = sha256HexToBase64(sha);
  await putWithProgress(
    init.upload_url,
    file,
    { 'Content-Type': uploadContentType(task), 'x-amz-checksum-sha256': checksum },
    (loaded) => update(task.id, { loaded }),
    (xhr) => refs.xhrs.current!.set(task.id, xhr),
  );
  refs.xhrs.current!.delete(task.id);
}

// Large-file path: multipart-init → parallel part PUTs (each returns an
// ETag) → multipart-complete. On abort, fire-and-forget multipart-abort
// to free the R2 state.
async function runMultipart(
  task: UploadTask,
  file: File,
  sha: string,
  ac: AbortController,
  update: (id: string, patch: Partial<UploadTask>) => void,
  refs: TaskRefs,
  partProgress: React.RefObject<Map<string, number[]>>,
) {
  const initResp = await fetch('/api/admin/images/multipart-init', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sha256: sha,
      size: file.size,
      name: task.imageName,
      content_type: 'application/octet-stream',
      part_size: PART_SIZE,
    }),
    signal: ac.signal,
  });
  if (!initResp.ok) throw new Error(await readError(initResp));
  const init = (await initResp.json()) as {
    upload_id?: string;
    key?: string;
    part_size?: number;
    part_urls?: string[];
    already_stored?: boolean;
  };
  if (init.already_stored) {
    update(task.id, { loaded: file.size });
    // Object already stored, and commit still runs so metadata is registered.
    const commitResp = await fetch('/api/admin/images/presign-commit', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(commitBody(task, sha)),
      signal: ac.signal,
    });
    if (!commitResp.ok) throw new Error(await readError(commitResp));
    return;
  }
  if (!init.upload_id || !init.key || !init.part_urls || init.part_urls.length === 0) {
    throw new Error('multipart-init returned incomplete payload');
  }

  const uploadID = init.upload_id;
  const key = init.key;
  const partSize = init.part_size ?? PART_SIZE;
  const urls = init.part_urls;
  const numParts = urls.length;

  // Validate every part URL before sending any bytes (FE-SEC-02).
  await Promise.all(urls.map((u) => validatePresignedUrl(u, ac.signal)));

  const loadedPerPart = new Array<number>(numParts).fill(0);
  partProgress.current!.set(task.id, loadedPerPart);

  const etags: (string | null)[] = new Array(numParts).fill(null);

  // Track in-flight XHRs so cancel can abort them.
  const partXHRs: Map<number, XMLHttpRequest> = new Map();
  refs.xhrs.current!.set(task.id, {
    abort: () => {
      partXHRs.forEach((x) => {
        x.abort();
      });
    },
  } as XMLHttpRequest);

  // Coalesce multi-worker progress to one setState per frame; otherwise
  // ~40 re-renders/sec each iterate the full task list.
  let rafScheduled = false;
  const recalcLoaded = () => {
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(() => {
      rafScheduled = false;
      let total = 0;
      for (const n of loadedPerPart) total += n;
      update(task.id, { loaded: total });
    });
  };

  // Worker pool: pull next part index from a shared cursor.
  let nextIdx = 0;
  const worker = async () => {
    while (true) {
      if (ac.signal.aborted) return;
      const i = nextIdx++;
      if (i >= numParts) return;
      const partNumber = i + 1;
      const start = i * partSize;
      const end = Math.min(start + partSize, file.size);
      const blob = file.slice(start, end);
      const etag = await putPartWithProgress(
        urls[i],
        blob,
        (loaded) => {
          loadedPerPart[i] = loaded;
          recalcLoaded();
        },
        (xhr) => partXHRs.set(partNumber, xhr),
      );
      partXHRs.delete(partNumber);
      etags[i] = etag;
    }
  };

  // Best-effort abort so R2 doesn't keep the half-uploaded parts.
  const abortMultipart = () => {
    void fetch('/api/admin/images/multipart-abort', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ upload_id: uploadID, key }),
    });
  };

  try {
    await Promise.all(
      Array.from({ length: Math.min(PART_CONCURRENCY, numParts) }, () => worker()),
    );
  } catch (e) {
    abortMultipart();
    throw e;
  }

  if (ac.signal.aborted) {
    abortMultipart();
    return;
  }

  // Phase 3: complete (this is what flips status to 'committing' for
  // the multipart path - single-PUT does it in the outer run).
  update(task.id, { status: 'committing', loaded: file.size });
  const parts = etags.map((etag, i) => ({ part_number: i + 1, etag: etag ?? '' }));
  const completeResp = await fetch('/api/admin/images/multipart-complete', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      upload_id: uploadID,
      key,
      sha256: sha,
      name: task.imageName,
      content_type: 'application/octet-stream',
      os_family: task.osFamily ?? '',
      parts,
    }),
    signal: ac.signal,
  });
  if (!completeResp.ok) throw new Error(await readError(completeResp));
}

// Memoized storage-info response. Holds the backend kind used to validate
// presigned URLs (FE-SEC-02).
interface StorageInfo {
  backendKind: string; // 'r2' | 'minio' | 's3' | ''
}

// Process-wide memo of /api/admin/storage/info. Cleared on logout via
// resetStorageInfo() so a different operator (or a backend reconfig between
// sessions) never reuses the prior session's storage routing (FE-SEC-01).
let storageInfoMemo: Promise<StorageInfo> | null = null;

// Drops the cached storage-info promise. Called from AuthContext.logout so the
// next session re-probes the backend instead of trusting stale module state.
export function resetStorageInfo(): void {
  storageInfoMemo = null;
}

function loadStorageInfo(signal: AbortSignal): Promise<StorageInfo> {
  if (storageInfoMemo) return storageInfoMemo;
  storageInfoMemo = (async (): Promise<StorageInfo> => {
    try {
      const resp = await fetch('/api/admin/storage/info', {
        credentials: 'include',
        signal,
      });
      if (!resp.ok) return { backendKind: '' };
      const j = (await resp.json()) as {
        backend_kind?: string;
      };
      return {
        backendKind: typeof j.backend_kind === 'string' ? j.backend_kind : '',
      };
    } catch {
      return { backendKind: '' };
    }
  })();
  return storageInfoMemo;
}

// Validates a presigned URL returned by the backend before PUT-ing file bytes
// to it (FE-SEC-02). Defense-in-depth against a compromised presign-init
// response redirecting multi-GB uploads to an attacker origin.
//
// Security boundary: server-side /api/admin enforcement is authoritative;
// this check only limits the blast radius of a backend-side presign compromise.
//
// Rules derived from backend_kind:
//   r2         → must be https + host ends with .r2.cloudflarestorage.com
//   s3         → must be https + host ends with .amazonaws.com
//   minio / '' → must be https (external MinIO; operator-configured host unknown)
async function validatePresignedUrl(url: string, signal: AbortSignal): Promise<void> {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error('presigned URL is not a valid URL');
  }
  if (parsed.protocol !== 'https:') {
    throw new Error(`presigned URL must use https (got ${parsed.protocol})`);
  }

  const { backendKind } = await loadStorageInfo(signal);
  if (backendKind === 'r2') {
    if (!parsed.hostname.endsWith('.r2.cloudflarestorage.com')) {
      throw new Error(
        `presigned URL host ${parsed.hostname} is not a valid R2 endpoint`,
      );
    }
  } else if (backendKind === 's3') {
    if (!parsed.hostname.endsWith('.amazonaws.com')) {
      throw new Error(
        `presigned URL host ${parsed.hostname} is not a valid S3 endpoint`,
      );
    }
  }
  // minio / '' / unknown: https scheme check above is the gate.
}

// Streaming sha256 in a Web Worker so the main thread stays responsive
// (~25 s for 14 GB). Abort posts {type:'abort'} so the worker returns early.
//
// Exported because the submit page hashes for a second reason: to ask whether
// this org has already analysed these bytes, which is a question about the
// DIGEST and cannot be answered from the filename. One worker rather than a
// second implementation of the same stream.
export function hashFileSHA256(
  file: File,
  signal: AbortSignal,
  onProgress: (loaded: number) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('@/lib/sha256.worker.ts', import.meta.url), {
      type: 'module',
    });
    const cleanup = () => {
      signal.removeEventListener('abort', onAbort);
      worker.terminate();
    };
    const onAbort = () => {
      worker.postMessage({ type: 'abort' });
      cleanup();
      reject(new DOMException('aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
    worker.onmessage = (e: MessageEvent<{ type: string; loaded?: number; hex?: string; message?: string }>) => {
      if (e.data.type === 'progress' && typeof e.data.loaded === 'number') {
        onProgress(e.data.loaded);
      } else if (e.data.type === 'done' && typeof e.data.hex === 'string') {
        cleanup();
        resolve(e.data.hex);
      } else if (e.data.type === 'error') {
        cleanup();
        reject(new Error(e.data.message ?? 'hash worker failed'));
      }
    };
    worker.onerror = (err) => {
      cleanup();
      // ErrorEvent.message is frequently the opaque "Script error." - fall back
      // to filename:line so the failure is at least diagnosable (FE-QUAL-04).
      const where = err.filename ? ` (${err.filename}:${err.lineno})` : '';
      reject(new Error(`hash worker failed: ${err.message || 'unknown error'}${where}`));
    };
    worker.postMessage({ type: 'hash', file });
  });
}

// Single-PUT variant. Signed Content-Type + x-amz-checksum-sha256 headers
// are mandatory on R2 single PutObject.
function putWithProgress(
  url: string,
  file: Blob,
  headers: Record<string, string>,
  onProgress: (loaded: number) => void,
  registerXHR: (xhr: XMLHttpRequest) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    registerXHR(xhr);
    xhr.open('PUT', url);
    xhr.withCredentials = false;
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`R2 PUT ${xhr.status}: ${xhr.responseText.slice(0, 256)}`));
    };
    xhr.onerror = () => reject(new Error('network error during R2 PUT'));
    xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'));
    xhr.send(file);
  });
}

// Multipart variant - PUTs one part and resolves with R2's ETag (needed for
// CompleteMultipartUpload). No checksum/content-type headers: the signed URL
// already binds bucket+key+partNumber+uploadID.
function putPartWithProgress(
  url: string,
  body: Blob,
  onProgress: (loaded: number) => void,
  registerXHR: (xhr: XMLHttpRequest) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    registerXHR(xhr);
    xhr.open('PUT', url);
    xhr.withCredentials = false;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag') || '';
        if (!etag) {
          reject(new Error('R2 UploadPart succeeded but returned no ETag'));
          return;
        }
        resolve(etag);
      } else {
        reject(new Error(`R2 UploadPart ${xhr.status}: ${xhr.responseText.slice(0, 256)}`));
      }
    };
    xhr.onerror = () => reject(new Error('network error during R2 UploadPart'));
    xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'));
    xhr.send(body);
  });
}

function sha256HexToBase64(hex: string): string {
  const bytes = new Uint8Array(32);
  for (let i = 0; i < 32; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

async function readError(resp: Response): Promise<string> {
  try {
    const j = await resp.json();
    if (typeof j === 'object' && j && 'error' in j) return String((j as { error: unknown }).error);
  } catch {}
  return `${resp.status} ${resp.statusText}`;
}
